import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import ProductSearchBox from "../components/ProductSearchBox";
import { Filter, X } from "lucide-react";

export default function CatalogPage({ categoryMode = false }) {
    const [params, setParams] = useSearchParams();
    const { slug } = useParams();
    const [cats, setCats] = useState([]);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [searchInput, setSearchInput] = useState(params.get("q") || "");

    const q = params.get("q") || "";
    const categoryId = params.get("category_id") || "";
    const minPrice = params.get("min_price") || "";
    const maxPrice = params.get("max_price") || "";
    const inStock = params.get("in_stock") === "1";
    const featured = params.get("featured") === "1";
    const sort = params.get("sort") || "newest";

    useEffect(() => {
        setSearchInput(q);
    }, [q]);

    useEffect(() => {
        const t = setTimeout(() => {
            if (searchInput === q) return;
            const next = new URLSearchParams(params);
            if (searchInput.trim()) next.set("q", searchInput.trim());
            else next.delete("q");
            setParams(next, { replace: true });
        }, 200);
        return () => clearTimeout(t);
    }, [searchInput]);

    const set = (key, val) => {
        const next = new URLSearchParams(params);
        if (val === "" || val === false || val == null) next.delete(key);
        else next.set(key, typeof val === "boolean" ? "1" : val);
        setParams(next, { replace: true });
    };

    useEffect(() => {
        api.get("/categories").then((r) => setCats(r.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        const reqParams = {
            q: q || undefined,
            category_id: categoryId || undefined,
            category_slug: categoryMode ? slug : undefined,
            min_price: minPrice || undefined,
            max_price: maxPrice || undefined,
            in_stock: inStock ? true : undefined,
            featured: featured ? true : undefined,
            sort,
            limit: 60,
        };
        api.get("/products", { params: reqParams })
            .then((r) => {
                setItems(r.data.items || []);
                setTotal(r.data.total || 0);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [q, categoryId, minPrice, maxPrice, inStock, featured, sort, categoryMode, slug]);

    const currentCat = useMemo(() => {
        if (categoryMode) return cats.find((c) => c.slug === slug);
        if (categoryId) return cats.find((c) => c.id === categoryId);
        return null;
    }, [cats, categoryId, categoryMode, slug]);

    let heading = "Todos los productos";
    if (currentCat) heading = currentCat.name;
    else if (q) heading = `Resultados para "${q}"`;

    return (
        <div className="w-full max-w-7xl mx-auto px-4 py-10 min-w-0 box-border" data-testid="catalog-page">
            <div className="border-b-2 border-[var(--lz-navy)] pb-6 mb-8 flex flex-col gap-4 min-w-0">
                <div className="min-w-0">
                    <span className="lz-section-label">Catálogo</span>
                    <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight break-words" data-testid="catalog-title">{heading}</h1>
                    <p className="text-sm text-[var(--lz-navy)]/60 font-mono mt-1">{total} productos</p>
                </div>
                <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch gap-2 w-full min-w-0">
                    <div className="w-full sm:flex-1 sm:min-w-[200px] min-w-0">
                        <ProductSearchBox
                            variant="catalog"
                            showStock={false}
                            value={searchInput}
                            onQueryChange={setSearchInput}
                            limit={8}
                            placeholder="Buscar en catálogo…"
                            inputTestId="catalog-search-input"
                        />
                    </div>
                    <select
                        value={sort}
                        onChange={(e) => set("sort", e.target.value)}
                        className="lz-input !py-2 !px-3 text-xs uppercase font-bold w-full sm:w-auto sm:min-w-[10rem]"
                        data-testid="sort-select"
                    >
                        <option value="newest">Más recientes</option>
                        <option value="price_asc">Precio: menor a mayor</option>
                        <option value="price_desc">Precio: mayor a menor</option>
                        <option value="name">Nombre A-Z</option>
                    </select>
                    <button
                        type="button"
                        onClick={() => setFiltersOpen((o) => !o)}
                        className="lz-btn lz-btn-outline lg:hidden w-full sm:w-auto"
                        data-testid="filters-toggle"
                    >
                        <Filter className="w-4 h-4" /> Filtros
                    </button>
                </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 min-w-0">
                <aside className={`lg:col-span-3 min-w-0 ${filtersOpen ? "block" : "hidden lg:block"}`} data-testid="filters-panel">
                    <div className="border-2 border-[var(--lz-navy)] p-5 space-y-6 bg-white sticky top-24">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--lz-orange)]">Filtros</span>
                            <button type="button" onClick={() => { setParams({}); setSearchInput(""); }} className="text-xs underline" data-testid="filters-clear">Limpiar</button>
                        </div>
                        {!categoryMode && (
                            <div>
                                <div className="text-xs font-bold uppercase mb-2">Categoría</div>
                                <div className="space-y-1 max-h-60 overflow-auto pr-1">
                                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                                        <input
                                            type="radio"
                                            name="cat"
                                            checked={!categoryId}
                                            onChange={() => set("category_id", "")}
                                            data-testid="filter-cat-all"
                                        />
                                        <span>Todas</span>
                                    </label>
                                    {cats.map((c) => (
                                        <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                            <input
                                                type="radio"
                                                name="cat"
                                                checked={categoryId === c.id}
                                                onChange={() => set("category_id", c.id)}
                                                data-testid={`filter-cat-${c.slug}`}
                                            />
                                            <span>{c.name}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div>
                            <div className="text-xs font-bold uppercase mb-2">Precio (MXN)</div>
                            <div className="flex gap-2">
                                <input type="number" placeholder="Min" value={minPrice} onChange={(e) => set("min_price", e.target.value)} className="lz-input text-sm" data-testid="filter-min-price" />
                                <input type="number" placeholder="Max" value={maxPrice} onChange={(e) => set("max_price", e.target.value)} className="lz-input text-sm" data-testid="filter-max-price" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input type="checkbox" checked={featured} onChange={(e) => set("featured", e.target.checked)} data-testid="filter-featured" />
                                <span>Destacados</span>
                            </label>
                        </div>
                        {q && (
                            <div className="flex items-center gap-2 text-xs">
                                <span className="font-bold uppercase">Búsqueda:</span>
                                <span className="font-mono">{q}</span>
                                <button type="button" onClick={() => { set("q", ""); setSearchInput(""); }} className="ml-auto" data-testid="filter-clear-q"><X className="w-3 h-3" /></button>
                            </div>
                        )}
                    </div>
                </aside>

                <main className="lg:col-span-9 min-w-0">
                    {(() => {
                        if (loading) {
                            return (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                    {["s1", "s2", "s3", "s4", "s5", "s6"].map((k) => (
                                        <div key={k} className="aspect-[3/4] border border-[var(--lz-navy)] bg-[#f3f4f6] animate-pulse" />
                                    ))}
                                </div>
                            );
                        }
                        if (items.length === 0) {
                            return (
                                <div className="border-2 border-dashed border-[var(--lz-navy)]/30 p-12 text-center">
                                    <div className="font-display text-2xl font-bold uppercase">Sin resultados</div>
                                    <p className="text-sm text-[var(--lz-navy)]/60 mt-2">Prueba con otros filtros o búsqueda diferente.</p>
                                </div>
                            );
                        }
                        return (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
                                {items.map((p) => <ProductCard key={p.id} product={p} showStock={false} />)}
                            </div>
                        );
                    })()}
                </main>
            </div>
        </div>
    );
}
